import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, BookOpen, Trophy, TrendingUp, 
  FileText, Upload, Trash2, ExternalLink, Loader2,
  CheckCircle, XCircle, Plus, ChevronDown, ChevronUp,
  Sparkles, Brain, RefreshCw, AlertCircle, Search, Filter, Lock,
  Clock, Target
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminProtected, { useAccessType } from '../components/AdminProtected';
import AttendanceManager from '../components/AttendanceManager';
import MockQuestionGenerator from '../components/MockQuestionGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SchoolDetailPage = () => {
  const { schoolId } = useParams();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const studentsRef = useRef(null);
  const { isTeacherAccess } = useAccessType();
  
  const [school, setSchool] = useState(null);
  const [students, setStudents] = useState([]);
  const [assignedStudentIds, setAssignedStudentIds] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [studentsOpen, setStudentsOpen] = useState(true);  // Open by default
  const [studentSearch, setStudentSearch] = useState('');
  const [studentBranchFilter, setStudentBranchFilter] = useState('all');
  
  // AI Question Generator state
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pdfMaterials, setPdfMaterials] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [numQuestions, setNumQuestions] = useState(20);
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [generationResult, setGenerationResult] = useState(null);
  const [finalExamInfo, setFinalExamInfo] = useState(null);
  const [isQuestionAdmin, setIsQuestionAdmin] = useState(false);
  
  // PDF Upload state for AI Generator
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [newPdfMaterial, setNewPdfMaterial] = useState({
    title: '',
    description: '',
    file_url: ''
  });
  
  const [newMaterial, setNewMaterial] = useState({
    title: '',
    description: '',
    file_url: '',
    file_name: '',
    file_type: 'link'
  });

  // Fetch assigned students for teachers - requires user to be logged in
  const fetchAssignedStudents = useCallback(async () => {
    if (!isTeacherAccess) return;
    if (authLoading) return; // Wait for auth to finish loading
    if (!token) {
      console.log('Teacher access: No token - teacher must log in to see assigned students');
      setAssignedStudentIds([]);
      return;
    }
    
    try {
      // Use the authenticated endpoint to get THIS teacher's assigned students
      const response = await fetch(`${BACKEND_URL}/api/teachers/my-students?school_id=${schoolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Get the student IDs assigned to this teacher
        const assignedIds = data.students.map(s => s.id);
        setAssignedStudentIds(assignedIds);
      } else if (response.status === 403) {
        // User is not a teacher - show all students
        console.log('User is not registered as a teacher');
        setAssignedStudentIds([]);
      }
    } catch (error) {
      console.error('Error fetching assigned students:', error);
      setAssignedStudentIds([]);
    }
  }, [isTeacherAccess, schoolId, token, authLoading]);

  // Computed: Filter students for teacher access
  // If teacher access and logged in with assigned students, show only those
  // If teacher access but not logged in or no assignments, show empty (require login)
  const displayStudents = isTeacherAccess 
    ? (token && assignedStudentIds.length > 0 
        ? students.filter(s => assignedStudentIds.includes(s.id))
        : (token ? students.filter(s => assignedStudentIds.includes(s.id)) : []))
    : students;
    
  // Check if teacher needs to log in - but only after auth has loaded
  const teacherNeedsLogin = isTeacherAccess && !authLoading && !token;

  const fetchSchoolData = useCallback(async () => {
    try {
      // Fetch school details
      const schoolRes = await fetch(`${BACKEND_URL}/api/schools/${schoolId}`);
      if (!schoolRes.ok) throw new Error('School not found');
      const schoolData = await schoolRes.json();
      setSchool(schoolData);

      // Fetch students
      const studentsRes = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/students`);
      const studentsData = await studentsRes.json();
      setStudents(studentsData);

      // Fetch materials
      const materialsRes = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/materials`);
      const materialsData = await materialsRes.json();
      setMaterials(materialsData);
      
      // Fetch final exam info
      const finalExamRes = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/final-exam`);
      if (finalExamRes.ok) {
        const finalExamData = await finalExamRes.json();
        setFinalExamInfo(finalExamData);
      }
      
      // Fetch PDF materials for question generation
      const pdfRes = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/materials-for-generation`);
      if (pdfRes.ok) {
        const pdfData = await pdfRes.json();
        setPdfMaterials(pdfData.pdf_materials || []);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    fetchSchoolData();
    
    // Auto-refresh every 10 seconds for real-time updates
    const intervalId = setInterval(() => {
      fetchSchoolData();
    }, 10000);
    
    // Refetch when page becomes visible or gains focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchSchoolData();
      }
    };
    
    const handleFocus = () => {
      fetchSchoolData();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchSchoolData]);

  // Fetch assigned students when teacher access
  useEffect(() => {
    if (isTeacherAccess) {
      fetchAssignedStudents();
    }
  }, [isTeacherAccess, fetchAssignedStudents]);

  // Check if user is a question admin
  useEffect(() => {
    const checkQuestionAdmin = async () => {
      if (!token) {
        setIsQuestionAdmin(false);
        return;
      }
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/is-question-admin`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setIsQuestionAdmin(data.is_question_admin);
        } else {
          setIsQuestionAdmin(false);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsQuestionAdmin(false);
      }
    };
    checkQuestionAdmin();
  }, [token]);

  // AI Question Generation handler
  const handleGenerateQuestions = async () => {
    if (pdfMaterials.length === 0) {
      toast.error('No PDF materials available. Please upload PDF training materials first.');
      return;
    }

    if (!token) {
      toast.error('Please log in to generate questions.');
      return;
    }

    setGenerating(true);
    setGenerationResult(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/generate-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          material_ids: selectedMaterials.length > 0 ? selectedMaterials : pdfMaterials.map(m => m.id),
          num_questions: numQuestions,
          replace_existing: replaceExisting
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle different error formats
        let errorMessage = 'Failed to generate questions';
        if (data.detail) {
          if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (Array.isArray(data.detail)) {
            errorMessage = data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
          }
        } else if (data.message) {
          errorMessage = data.message;
        }
        throw new Error(errorMessage);
      }

      setGenerationResult(data);
      toast.success(`Successfully generated ${data.questions_generated} questions!`);
      
      // Refresh final exam info
      const finalExamRes = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/final-exam`);
      if (finalExamRes.ok) {
        const finalExamData = await finalExamRes.json();
        setFinalExamInfo(finalExamData);
      }
    } catch (error) {
      console.error('Generate questions error:', error);
      toast.error(error.message || 'An error occurred while generating questions');
    } finally {
      setGenerating(false);
    }
  };

  const handleClearQuestions = async () => {
    if (!window.confirm('Are you sure you want to clear all final exam questions? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/final-exam/questions/clear`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to clear questions');
      }

      toast.success('All questions cleared');
      setFinalExamInfo(prev => prev ? { ...prev, question_count: 0, is_active: false } : null);
      setGenerationResult(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const toggleMaterialSelection = (materialId) => {
    setSelectedMaterials(prev => 
      prev.includes(materialId) 
        ? prev.filter(id => id !== materialId)
        : [...prev, materialId]
    );
  };

  // Upload PDF directly from AI Generator modal
  const handleUploadPdfForGeneration = async () => {
    if (!newPdfMaterial.title) {
      toast.error('Please enter a title for the PDF');
      return;
    }
    if (!newPdfMaterial.file_url) {
      toast.error('Please enter a PDF URL');
      return;
    }

    // Validate URL contains .pdf or is a valid URL
    const url = newPdfMaterial.file_url.toLowerCase();
    if (!url.includes('.pdf') && !url.includes('pdf')) {
      toast.error('Please enter a valid PDF URL');
      return;
    }

    setUploadingPdf(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/materials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          school_id: schoolId,
          title: newPdfMaterial.title,
          description: newPdfMaterial.description,
          file_url: newPdfMaterial.file_url,
          file_name: newPdfMaterial.title + '.pdf',
          file_type: 'pdf'
        })
      });

      if (!response.ok) throw new Error('Failed to upload PDF');

      toast.success('PDF added successfully!');
      setNewPdfMaterial({ title: '', description: '', file_url: '' });
      setShowUploadForm(false);
      
      // Refresh PDF materials list
      const pdfRes = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/materials-for-generation`);
      if (pdfRes.ok) {
        const pdfData = await pdfRes.json();
        setPdfMaterials(pdfData.pdf_materials || []);
      }
      
      // Also refresh the main materials list
      const materialsRes = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/materials`);
      const materialsData = await materialsRes.json();
      setMaterials(materialsData);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleAddMaterial = async () => {
    if (!newMaterial.title) {
      toast.error('Please enter a title');
      return;
    }

    setUploading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/materials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          school_id: schoolId,
          ...newMaterial
        })
      });

      if (!response.ok) throw new Error('Failed to add material');

      toast.success('Training material added successfully!');
      setDialogOpen(false);
      setNewMaterial({ title: '', description: '', file_url: '', file_name: '', file_type: 'link' });
      fetchSchoolData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Are you sure you want to delete this material?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/materials/${materialId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete material');

      toast.success('Material deleted');
      fetchSchoolData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleStudentsClick = () => {
    setStudentsOpen(!studentsOpen);
    if (!studentsOpen) {
      setTimeout(() => {
        studentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#5f7c8a]" />
      </div>
    );
  }

  if (!school) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 pt-20 pb-12 px-4 flex items-center justify-center">
          <Card className="text-center p-8">
            <p className="text-gray-500">School not found</p>
            <Link to="/schools">
              <Button className="mt-4">Back to Schools</Button>
            </Link>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/schools" 
            className="inline-flex items-center gap-2 text-[#5f7c8a] hover:text-[#4a6470] transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            Back to Schools
          </Link>

          {/* School Header */}
          <div className="bg-[#5f7c8a] rounded-lg p-8 mb-8 text-white">
            <h1 className="text-3xl font-bold mb-2">{school.name}</h1>
            <p className="text-white/80 mb-6">{school.exam_description}</p>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Clickable Enrolled Students Card */}
              <div 
                onClick={handleStudentsClick}
                className="bg-white/10 rounded-lg p-4 cursor-pointer hover:bg-white/20 transition-all duration-200 ring-2 ring-transparent hover:ring-white/30"
              >
                <div className="flex items-center justify-between">
                  <Users className="mb-2" />
                  {studentsOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
                <p className="text-2xl font-bold">{school.enrolled_students}</p>
                <p className="text-sm text-white/70">Enrolled Students</p>
                <p className="text-xs text-white/50 mt-1">Click to {studentsOpen ? 'hide' : 'view'} details</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <BookOpen className="mb-2" />
                <p className="text-2xl font-bold">{school.mock_exam?.question_count || school.question_count}</p>
                <p className="text-sm text-white/70">Mock Questions</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <Clock className="mb-2" />
                <p className="text-2xl font-bold">{school.mock_exam?.duration_minutes || 12}</p>
                <p className="text-sm text-white/70">Minutes</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <Target className="mb-2" />
                <p className="text-2xl font-bold">{school.mock_exam?.passing_score || 60}%</p>
                <p className="text-sm text-white/70">To Pass</p>
              </div>
            </div>
          </div>

          {/* Enrolled Students Section */}
          <div ref={studentsRef}>
            <Card className="mb-8 border-2 border-[#5f7c8a]/20">
              <CardHeader className="bg-gradient-to-r from-[#5f7c8a]/10 to-transparent">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Users className="text-[#5f7c8a]" />
                      {isTeacherAccess ? 'Assigned Students' : 'Enrolled Students'} ({displayStudents.length})
                    </CardTitle>
                    <CardDescription>
                      {isTeacherAccess 
                        ? 'Students assigned to you for this school' 
                        : 'Students registered for this school'}
                    </CardDescription>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setStudentsOpen(!studentsOpen)}
                    className="text-[#5f7c8a]"
                  >
                    {studentsOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </Button>
                </div>
              </CardHeader>
              
              {studentsOpen && (
                <CardContent>
                  {teacherNeedsLogin ? (
                    <div className="text-center py-8 text-amber-600 bg-amber-50 rounded-lg">
                      <Lock size={48} className="mx-auto mb-4 text-amber-400" />
                      <p className="font-medium">Login Required</p>
                      <p className="text-sm mt-2 text-amber-500">
                        Please log in with your teacher account to see your assigned students.
                      </p>
                      <Link to={`/login?redirect=${encodeURIComponent(`/school/${schoolId}`)}`}>
                        <Button className="mt-4 bg-amber-500 hover:bg-amber-600" data-testid="teacher-login-btn">
                          Log In
                        </Button>
                      </Link>
                    </div>
                  ) : displayStudents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Users size={48} className="mx-auto mb-4 text-gray-300" />
                      <p>{isTeacherAccess ? 'No students assigned to you yet' : 'No students enrolled yet'}</p>
                    </div>
                  ) : (
                    <>
                      {/* Search and Filter */}
                      <div className="flex flex-col md:flex-row gap-4 mb-4">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                          <Input
                            placeholder="Search by name, email, or phone..."
                            value={studentSearch}
                            onChange={(e) => setStudentSearch(e.target.value)}
                            className="pl-10"
                            data-testid="student-search"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Filter size={18} className="text-gray-400" />
                          <select
                            value={studentBranchFilter}
                            onChange={(e) => setStudentBranchFilter(e.target.value)}
                            className="border rounded-md px-3 py-2 text-sm bg-white"
                            data-testid="branch-filter"
                          >
                            <option value="all">All Branches</option>
                            {[...new Set(displayStudents.map(s => s.branch).filter(Boolean))].sort().map(branch => (
                              <option key={branch} value={branch}>{branch}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Students Table */}
                      <div className="overflow-x-auto border rounded-lg">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50">
                              <TableHead className="font-semibold">Student ID</TableHead>
                              <TableHead className="font-semibold">Name</TableHead>
                              <TableHead className="font-semibold">Email</TableHead>
                              <TableHead className="font-semibold">Phone</TableHead>
                              <TableHead className="font-semibold">Branch</TableHead>
                              <TableHead className="font-semibold">Constituency</TableHead>
                              <TableHead className="font-semibold">Exam Status</TableHead>
                              <TableHead className="font-semibold">Score</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {displayStudents
                              .filter(student => {
                                const searchLower = studentSearch.toLowerCase();
                                const matchesSearch = !studentSearch || 
                                  student.name?.toLowerCase().includes(searchLower) ||
                                  student.email?.toLowerCase().includes(searchLower) ||
                                  student.phone?.includes(studentSearch) ||
                                  student.student_id?.toLowerCase().includes(searchLower) ||
                                  student.constituency?.toLowerCase().includes(searchLower);
                                const matchesBranch = studentBranchFilter === 'all' || 
                                  student.branch === studentBranchFilter;
                                return matchesSearch && matchesBranch;
                              })
                              .map((student) => (
                              <TableRow key={student.id} className="hover:bg-gray-50">
                                <TableCell>
                                  <Badge variant="outline" className="font-mono text-xs bg-blue-50 text-blue-700 border-blue-200">
                                    {student.student_id || '-'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="font-medium">{student.name}</TableCell>
                                <TableCell className="text-gray-600">{student.email}</TableCell>
                                <TableCell className="text-gray-600">{student.phone || '-'}</TableCell>
                                <TableCell>
                                  {student.branch ? (
                                    <Badge variant="outline" className="text-xs">{student.branch}</Badge>
                                  ) : '-'}
                                </TableCell>
                                <TableCell>
                                  {student.constituency ? (
                                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">{student.constituency}</Badge>
                                  ) : '-'}
                                </TableCell>
                                <TableCell>
                                  {student.exam_taken ? (
                                    student.exam_passed ? (
                                      <Badge className="bg-green-500">
                                        <CheckCircle size={12} className="mr-1" /> Passed
                                      </Badge>
                                    ) : (
                                      <Badge variant="destructive">
                                        <XCircle size={12} className="mr-1" /> Failed
                                      </Badge>
                                    )
                                  ) : (
                                    <Badge variant="secondary">Not Taken</Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {student.exam_taken ? `${student.exam_score?.toFixed(0)}%` : '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      
                      {/* Summary */}
                      <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
                        <span>
                          Showing {displayStudents.filter(s => {
                            const searchLower = studentSearch.toLowerCase();
                            const matchesSearch = !studentSearch || 
                              s.name?.toLowerCase().includes(searchLower) ||
                              s.email?.toLowerCase().includes(searchLower) ||
                              s.phone?.includes(studentSearch);
                            const matchesBranch = studentBranchFilter === 'all' || s.branch === studentBranchFilter;
                            return matchesSearch && matchesBranch;
                          }).length} of {displayStudents.length} students
                        </span>
                        <span className="text-green-600">
                          • {displayStudents.filter(s => s.exam_passed).length} passed
                        </span>
                        <span className="text-red-600">
                          • {displayStudents.filter(s => s.exam_taken && !s.exam_passed).length} failed
                        </span>
                        <span className="text-gray-500">
                          • {displayStudents.filter(s => !s.exam_taken).length} not taken
                        </span>
                      </div>
                    </>
                  )}
                </CardContent>
              )}
            </Card>
          </div>

          {/* Training Materials Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_ed2010dd-aa6f-4356-b1ff-4b0ef249594d/artifacts/u5gyqqrz_IMG_1754.png" 
                      alt="Training Materials"
                      className="w-8 h-8 object-contain"
                    />
                    Training Materials
                  </CardTitle>
                  <CardDescription>Resources and materials for this school</CardDescription>
                </div>
                {isAuthenticated && (
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#5f7c8a] hover:bg-[#4a6470]">
                        <Plus size={16} className="mr-2" /> Add Material
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Training Material</DialogTitle>
                        <DialogDescription>
                          Add a new training resource for this school
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Title *</Label>
                          <Input
                            value={newMaterial.title}
                            onChange={(e) => setNewMaterial(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="Enter material title"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Textarea
                            value={newMaterial.description}
                            onChange={(e) => setNewMaterial(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Brief description of the material"
                            rows={3}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>File URL / Link</Label>
                          <Input
                            value={newMaterial.file_url}
                            onChange={(e) => setNewMaterial(prev => ({ ...prev, file_url: e.target.value }))}
                            placeholder="https://drive.google.com/... or any URL"
                          />
                          <p className="text-xs text-gray-500">
                            Paste a Google Drive link, Dropbox link, or any external URL
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>File Name (optional)</Label>
                          <Input
                            value={newMaterial.file_name}
                            onChange={(e) => setNewMaterial(prev => ({ ...prev, file_name: e.target.value }))}
                            placeholder="e.g., Lesson_1_Introduction.pdf"
                          />
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                          <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleAddMaterial}
                            disabled={uploading}
                            className="bg-[#5f7c8a] hover:bg-[#4a6470]"
                          >
                            {uploading ? (
                              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Adding...</>
                            ) : (
                              <><Upload size={16} className="mr-2" /> Add Material</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {materials.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No training materials available yet</p>
                  {isAuthenticated && (
                    <p className="text-sm mt-2">Click &quot;Add Material&quot; to upload resources</p>
                  )}
                </div>
              ) : (
                <div className="grid gap-4">
                  {materials.map((material) => (
                    <div 
                      key={material.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="bg-[#5f7c8a]/10 p-2 rounded-lg">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_ed2010dd-aa6f-4356-b1ff-4b0ef249594d/artifacts/u5gyqqrz_IMG_1754.png" 
                            alt="Material"
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-800">{material.title}</h4>
                          {material.description && (
                            <p className="text-sm text-gray-500">{material.description}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            Uploaded by {material.uploaded_by}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {material.file_url && (
                          material.file_type === 'pptx' || material.file_name?.endsWith('.pptx') ? (
                            <a 
                              href={material.file_url} 
                              download={material.file_name || 'presentation.pptx'}
                            >
                              <Button variant="outline" size="sm" className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100">
                                <ExternalLink size={14} className="mr-1" /> Download for PowerPoint
                              </Button>
                            </a>
                          ) : (
                            <a 
                              href={material.file_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <Button variant="outline" size="sm">
                                <ExternalLink size={14} className="mr-1" /> Open
                              </Button>
                            </a>
                          )
                        )}
                        {isAuthenticated && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteMaterial(material.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Attendance Tracker */}
          <AttendanceManager 
            schoolId={schoolId} 
            schoolName={school?.name} 
            students={displayStudents} 
          />

          {/* Mock Question Generator - Only visible to question admins */}
          {isQuestionAdmin ? (
            <MockQuestionGenerator
              schoolId={schoolId}
              schoolName={school?.name}
              token={token}
              isAdmin={isQuestionAdmin}
            />
          ) : isAuthenticated && (
            <Card className="bg-gray-50 border-gray-200 mt-8">
              <CardContent className="py-6">
                <div className="flex items-center gap-3 text-gray-500">
                  <Lock size={20} className="text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-600">Mock Exam Question Generator</p>
                    <p className="text-sm">This feature is restricted to authorized question administrators. Contact your admin for access.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Laikos Exam Questions Generator Section - Only visible to question admins */}
          {isQuestionAdmin ? (
          <Card className="border-2 border-dashed border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-purple-800">
                    <Brain className="text-purple-600" />
                    Laikos Exam Questions Generator
                  </CardTitle>
                  <CardDescription className="text-purple-600">
                    Generate final exam questions from uploaded PDF materials using AI
                  </CardDescription>
                </div>
                <Dialog open={generatorOpen} onOpenChange={setGeneratorOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                      data-testid="open-question-generator-btn"
                    >
                      <Sparkles size={16} className="mr-2" /> Generate Questions
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Brain className="text-purple-600" />
                        Laikos Exam Questions Generator
                      </DialogTitle>
                      <DialogDescription>
                        Use AI to automatically generate exam questions from your uploaded PDF training materials.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-6 mt-4">
                      {/* Current Exam Status */}
                      {finalExamInfo && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-700 mb-2">Current Final Exam Status</h4>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1">
                              <BookOpen size={14} className="text-gray-500" />
                              {finalExamInfo.question_count || 0} Questions
                            </span>
                            <Badge variant={finalExamInfo.is_active ? "default" : "secondary"}>
                              {finalExamInfo.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={handleClearQuestions}
                            data-testid="clear-questions-btn"
                          >
                            <Trash2 size={14} className="mr-1" /> Clear All Questions
                          </Button>
                        </div>
                      )}
                      
                      {/* Show clear option even if no finalExamInfo */}
                      {!finalExamInfo && (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <p className="text-sm text-blue-800 mb-2">No final exam configured yet.</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={handleClearQuestions}
                            data-testid="clear-questions-btn-init"
                          >
                            <Trash2 size={14} className="mr-1" /> Reset/Clear Any Existing Data
                          </Button>
                        </div>
                      )}

                      {/* PDF Materials Selection */}
                      <div className="space-y-3">
                        <Label className="text-base font-medium">Select PDF Materials</Label>
                        <Button
                          variant="default"
                          size="default"
                          onClick={() => setShowUploadForm(!showUploadForm)}
                          className="bg-purple-600 hover:bg-purple-700 text-white font-medium w-full"
                          data-testid="toggle-upload-form-btn"
                        >
                          <Plus size={18} className="mr-2" />
                          {showUploadForm ? 'Cancel Upload' : 'Upload New PDF'}
                        </Button>

                        {/* Upload PDF Form */}
                        {showUploadForm && (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                            <p className="text-sm text-purple-700 font-medium">Add PDF for Question Generation</p>
                            <div className="space-y-2">
                              <Input
                                placeholder="PDF Title *"
                                value={newPdfMaterial.title}
                                onChange={(e) => setNewPdfMaterial(prev => ({ ...prev, title: e.target.value }))}
                                className="bg-white"
                              />
                              <Input
                                placeholder="PDF URL (Google Drive, Dropbox, direct link) *"
                                value={newPdfMaterial.file_url}
                                onChange={(e) => setNewPdfMaterial(prev => ({ ...prev, file_url: e.target.value }))}
                                className="bg-white"
                              />
                              <Input
                                placeholder="Description (optional)"
                                value={newPdfMaterial.description}
                                onChange={(e) => setNewPdfMaterial(prev => ({ ...prev, description: e.target.value }))}
                                className="bg-white"
                              />
                              <p className="text-xs text-purple-600">
                                Tip: Use a direct download link for best results. Google Drive links work if sharing is enabled.
                              </p>
                            </div>
                            <Button
                              onClick={handleUploadPdfForGeneration}
                              disabled={uploadingPdf || !newPdfMaterial.title || !newPdfMaterial.file_url}
                              className="w-full bg-purple-600 hover:bg-purple-700"
                              data-testid="upload-pdf-btn"
                            >
                              {uploadingPdf ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Adding PDF...</>
                              ) : (
                                <><Upload size={14} className="mr-2" /> Add PDF</>
                              )}
                            </Button>
                          </div>
                        )}

                        {pdfMaterials.length === 0 && !showUploadForm ? (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
                            <div className="flex items-start gap-2">
                              <AlertCircle size={20} className="mt-0.5" />
                              <div>
                                <p className="font-medium">No PDF materials found</p>
                                <p className="text-sm mt-1">
                                  Click &quot;Upload PDF&quot; above to add training materials. The AI will analyze the PDF content to generate relevant exam questions.
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : pdfMaterials.length > 0 && (
                          <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                            {pdfMaterials.map((material) => (
                              <div 
                                key={material.id}
                                className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                                  selectedMaterials.includes(material.id) || selectedMaterials.length === 0
                                    ? 'bg-purple-50 border border-purple-200' 
                                    : 'hover:bg-gray-50'
                                }`}
                                onClick={() => toggleMaterialSelection(material.id)}
                              >
                                <Checkbox 
                                  checked={selectedMaterials.includes(material.id) || selectedMaterials.length === 0}
                                  className="data-[state=checked]:bg-purple-600"
                                />
                                <FileText size={16} className="text-red-500" />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{material.title}</p>
                                  {material.description && (
                                    <p className="text-xs text-gray-500 truncate">{material.description}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                            <p className="text-xs text-gray-500 mt-2">
                              {selectedMaterials.length === 0 
                                ? `All ${pdfMaterials.length} PDF(s) will be used` 
                                : `${selectedMaterials.length} PDF(s) selected`}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Number of Questions */}
                      <div className="space-y-2">
                        <Label htmlFor="numQuestions">Number of Questions to Generate</Label>
                        <Input
                          id="numQuestions"
                          type="number"
                          min={5}
                          max={200}
                          value={numQuestions}
                          onChange={(e) => setNumQuestions(parseInt(e.target.value) || 10)}
                          className="w-32"
                          data-testid="num-questions-input"
                        />
                        <p className="text-xs text-gray-500">
                          Enter any number from 5 to 200 questions
                        </p>
                      </div>

                      {/* Replace or Append */}
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="replaceExisting"
                          checked={replaceExisting}
                          onCheckedChange={setReplaceExisting}
                          className="data-[state=checked]:bg-purple-600"
                        />
                        <Label htmlFor="replaceExisting" className="cursor-pointer">
                          Replace existing questions (uncheck to append)
                        </Label>
                      </div>

                      {/* Generation Result Preview */}
                      {generationResult && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                            <CheckCircle size={18} />
                            Questions Generated Successfully!
                          </div>
                          <div className="text-sm text-green-600 space-y-1">
                            <p>• Generated: {generationResult.questions_generated} questions</p>
                            <p>• Total in exam: {generationResult.total_questions} questions</p>
                            <p>• Status: {generationResult.is_active ? 'Exam is now active' : 'Pending activation'}</p>
                          </div>
                          {generationResult.preview && generationResult.preview.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-green-200">
                              <p className="text-xs font-medium text-green-700 mb-2">Sample Questions:</p>
                              <div className="space-y-2">
                                {generationResult.preview.slice(0, 2).map((q, idx) => (
                                  <div key={idx} className="text-xs bg-white/50 rounded p-2">
                                    <p className="font-medium">{idx + 1}. {q.question_text}</p>
                                    <p className="text-green-600 mt-1">
                                      Type: {q.question_type === 'multiple_choice' ? 'Multiple Choice' : 'True/False'}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Login Warning */}
                      {!token && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <p className="text-sm text-amber-800 font-medium">
                            ⚠️ Please log in as a student to generate questions.
                          </p>
                          <p className="text-xs text-amber-600 mt-1">
                            You need to be logged in to use the AI question generator.
                          </p>
                        </div>
                      )}

                      {/* Generate Button */}
                      <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button variant="outline" onClick={() => setGeneratorOpen(false)}>
                          Close
                        </Button>
                        <Button
                          onClick={handleGenerateQuestions}
                          disabled={generating || pdfMaterials.length === 0 || !token}
                          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                          data-testid="generate-questions-btn"
                        >
                          {generating ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Generating... (This may take a minute)
                            </>
                          ) : (
                            <>
                              <Sparkles size={16} className="mr-2" />
                              Generate {numQuestions} Questions
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* PDF Materials Count */}
                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center gap-2 text-purple-600 mb-1">
                    <FileText size={18} />
                    <span className="font-medium">PDF Materials</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{pdfMaterials.length}</p>
                  <p className="text-xs text-gray-500">Available for generation</p>
                </div>

                {/* Current Questions */}
                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center gap-2 text-indigo-600 mb-1">
                    <BookOpen size={18} />
                    <span className="font-medium">Final Exam Questions</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{finalExamInfo?.question_count || 0}</p>
                  <p className="text-xs text-gray-500">Currently in exam</p>
                </div>

                {/* Exam Status */}
                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center gap-2 text-green-600 mb-1">
                    <Trophy size={18} />
                    <span className="font-medium">Exam Status</span>
                  </div>
                  <Badge 
                    variant={finalExamInfo?.is_active ? "default" : "secondary"}
                    className={finalExamInfo?.is_active ? "bg-green-500" : ""}
                  >
                    {finalExamInfo?.is_active ? "Active" : "Not Active"}
                  </Badge>
                  <p className="text-xs text-gray-500 mt-1">
                    {finalExamInfo?.is_active 
                      ? "Students can take the final exam" 
                      : "Generate questions to activate"}
                  </p>
                </div>
              </div>

              <div className="mt-4 p-3 bg-purple-100/50 rounded-lg">
                <p className="text-sm text-purple-700">
                  <Sparkles size={14} className="inline mr-1" />
                  <strong>How it works:</strong> Upload PDF training materials, then click &quot;Generate Questions&quot;. 
                  Our AI will analyze the content and create relevant multiple-choice and true/false questions automatically.
                </p>
              </div>
            </CardContent>
          </Card>
          ) : isAuthenticated && (
            <Card className="bg-gray-50 border-gray-200 mt-4">
              <CardContent className="py-6">
                <div className="flex items-center gap-3 text-gray-500">
                  <Lock size={20} className="text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-600">Final Exam Question Generator</p>
                    <p className="text-sm">This feature is restricted to authorized question administrators. Contact your admin for access.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

const ProtectedSchoolDetailPage = () => (
  <AdminProtected>
    <SchoolDetailPage />
  </AdminProtected>
);

export default ProtectedSchoolDetailPage;
